import { useSearchParams } from 'react-router';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { useApi } from '@/hooks/use-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTimeMinute } from '@/lib/format';

// Mirrors src/server/queries/user-activity.ts.
interface ActivityPerson {
  id: string;
  name: string | null;
  email: string | null;
}
interface ActivityRow {
  at: string;
  user: ActivityPerson & { superAdmin: boolean };
  brands: string[];
  via: 'app' | 'mcp';
  impersonatedBy: ActivityPerson | null;
}
interface UserActivity {
  window: { from: string; before: string; olderBefore: string };
  rows: ActivityRow[];
}

const DAY_MS = 86_400_000;
const shiftDay = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

export function UserActivityPage() {
  const [params, setParams] = useSearchParams();
  const before = params.get('before');
  const { data, error, loading, refresh, lastFetched } = useApi<UserActivity>(
    before ? `/api/user-activity?before=${before}` : '/api/user-activity'
  );

  const goOlder = () => data && setParams({ before: data.window.olderBefore });
  const goNewer = () => {
    if (!data) return;
    const newer = shiftDay(data.window.before, 7);
    // Back at the latest page: drop the param so the window keeps following today.
    if (newer > new Date().toISOString().slice(0, 10)) setParams({});
    else setParams({ before: newer });
  };

  return (
    <div className="flex flex-col">
      <Header
        title="User Activity"
        lastFetched={lastFetched}
        onRefresh={refresh}
        loading={loading}
      />
      <div className="space-y-6 p-6">
        {error && <ErrorState message={error} onRetry={refresh} />}
        {loading && !data && <LoadingState rows={6} />}
        {data && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle>
                {data.window.from} – {shiftDay(data.window.before, -1)} (UTC days), newest first
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={goNewer} disabled={!before}>
                  Newer
                </Button>
                <Button variant="outline" size="sm" onClick={goOlder}>
                  Older
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {data.rows.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center text-sm">
                  No activity recorded yet.
                </p>
              ) : (
                <ActivityTable rows={data.rows} />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ActivityTable({ rows }: { rows: ActivityRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Brand</TableHead>
          <TableHead>Time</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={`${r.at}-${r.user.id}-${r.impersonatedBy?.id ?? ''}-${r.via}`}
            // Impersonated rows are a Super Admin's session, not the user's own
            // use: tinted so the difference can't be missed.
            className={r.impersonatedBy ? 'bg-amber-500/10 hover:bg-amber-500/15' : undefined}
          >
            <TableCell>
              <span className="flex items-center gap-2">
                {r.user.name ?? <span className="text-muted-foreground">Unknown user</span>}
                {r.user.superAdmin && <Badge variant="secondary">Super Admin</Badge>}
              </span>
            </TableCell>
            <TableCell className="text-muted-foreground">{r.user.email ?? r.user.id}</TableCell>
            <TableCell>
              {r.user.superAdmin ? (
                <span className="text-muted-foreground">All brands</span>
              ) : (
                r.brands.join(', ') || <span className="text-muted-foreground">None</span>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap">{formatDateTimeMinute(r.at)}</TableCell>
            <TableCell>
              <span className="flex flex-wrap gap-1">
                {r.impersonatedBy && (
                  <Badge className="bg-amber-500 text-white">
                    Impersonated by{' '}
                    {r.impersonatedBy.name ?? r.impersonatedBy.email ?? r.impersonatedBy.id}
                  </Badge>
                )}
                {r.via === 'mcp' && <Badge variant="outline">Ask Overhead</Badge>}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
